/**
 * Accounting Configuration Panel
 * Contains Chart of Accounts and Accounting Rules configuration
 * Used in the Admin Dashboard
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { accountingService } from '../../services/api/accountingService';
import { clientService, TradingEntity } from '../../services/api/clientService';
import { groupingsService } from '../../services/api/groupingsService';
import { GroupingsConfig } from '../../types/groupings';
import AlertModal from '../common/AlertModal';
import {
  AccountingConfig,
  Account,
  AccountCreate,
  AccountUpdate,
  AccountType,
  AccountingConcept,
  AmountSource,
  AccountingRule,
  AccountingRuleCreate,
  AccountingSplit,
  RuleConditions,
  EntryMode,
  CostCentre,
  CostCentreCreate,
  ACCOUNT_TYPE_PREFIXES,
  MULTI_CURRENCY,
} from '../../types/accounting';
import OutputFormatsSubTab from '../outputFormats/OutputFormatsSubTab';
import GenerationScheduleSubTab from './GenerationScheduleSubTab';
import ModificationSettingsSubTab from './ModificationSettingsSubTab';
import { Upload } from 'lucide-react';
import './AccountingConfigPanel.css';

type ConfigSubTab = 'accounts' | 'costCentres' | 'rules' | 'outputFormats' | 'generationSchedule' | 'modificationsettings';

const AccountingConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<ConfigSubTab>('accounts');

  // Config state
  const [config, setConfig] = useState<AccountingConfig | null>(null);
  const [editingCurrency, setEditingCurrency] = useState(false);
  const [newCurrency, setNewCurrency] = useState('');

  // Accounts state
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const [showOtherCurrencies, setShowOtherCurrencies] = useState(true);

  // Group collapse state (all collapsed by default)
  const [expandedGroups, setExpandedGroups] = useState<Set<AccountType>>(new Set());

  // Account form state
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [accountForm, setAccountForm] = useState<AccountCreate>({
    accountNumber: '',
    name: '',
    description: '',
    type: 'ASSET',
    currency: '',
  });
  const [currencyMode, setCurrencyMode] = useState<'single' | 'multi'>('single');
  const [accountFormErrors, setAccountFormErrors] = useState<string[]>([]);
  const [savingAccount, setSavingAccount] = useState(false);

  // All accounts (unfiltered) for rule form dropdowns
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);

  // Rules state
  const [rules, setRules] = useState<AccountingRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [rulesConceptFilter, setRulesConceptFilter] = useState<string>('');
  const [rulesSearchFilter, setRulesSearchFilter] = useState<string>('');
  const [showInactiveRules, setShowInactiveRules] = useState(false);
  const [expandedRuleGroups, setExpandedRuleGroups] = useState<Set<AccountingConcept>>(new Set());

  // Rule form state
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AccountingRule | null>(null);
  const [ruleForm, setRuleForm] = useState<AccountingRuleCreate>({
    name: '',
    description: '',
    concept: 'NEW_TRADE',
    conditions: undefined,
    debitAccount: '',
    creditAccount: '',
    debitCostCentre: '',
    creditCostCentre: '',
    debitSplits: undefined,
    creditSplits: undefined,
    amountSource: 'TRADE_AMOUNT_CCY1',
    currencyHandling: 'EVENT_CURRENCY',
    aggregated: false,
    entryMode: 'both',
    netSign: undefined,
    triggerState: undefined,
    accountingDate: undefined,
  });
  const [ruleFormErrors, setRuleFormErrors] = useState<string[]>([]);
  const [savingRule, setSavingRule] = useState(false);

  // Cost centres state
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [loadingCostCentres, setLoadingCostCentres] = useState(true);
  const [ccSearchFilter, setCcSearchFilter] = useState('');
  const [showInactiveCc, setShowInactiveCc] = useState(false);
  const [showCcForm, setShowCcForm] = useState(false);
  const [editingCc, setEditingCc] = useState<CostCentre | null>(null);
  const [ccForm, setCcForm] = useState<CostCentreCreate>({ code: '', description: '' });
  const [ccFormErrors, setCcFormErrors] = useState<string[]>([]);
  const [savingCc, setSavingCc] = useState(false);
  // All cost centres (unfiltered, for rule form dropdowns)
  const [allCostCentres, setAllCostCentres] = useState<CostCentre[]>([]);

  // Condition dropdowns data (for rule form)
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // ============================================
  // Rule Splits Upload Modal State (Iansa-only)
  // ============================================
  const IANSA_CLIENT_ID = 'kwLuR44uYzttQlnHhBYx';
  const canUploadRuleSplits = clientId === IANSA_CLIENT_ID && (
    ['client_admin', 'client_acc_user'].includes(user?.role || '') ||
    user?.email === 'ben.clark@palace.cl'
  );
  const [showRuleSplitsModal, setShowRuleSplitsModal] = useState(false);
  const [ruleSplitsFile, setRuleSplitsFile] = useState<File | null>(null);
  const [ruleSplitsUploading, setRuleSplitsUploading] = useState(false);
  const [ruleSplitsResult, setRuleSplitsResult] = useState<{
    updated: { rule: string; splits: { side: string; account: string; percentage: number }[] }[];
    notFound: string[];
    errors: string[];
    summary: { rulesUpdated: number; rulesNotFound: number; errors: number };
  } | null>(null);

  const handleRuleSplitsUpload = async () => {
    if (!clientId || !ruleSplitsFile || ruleSplitsUploading) return;
    setRuleSplitsUploading(true);
    setRuleSplitsResult(null);

    try {
      const response = await accountingService.uploadRuleSplits(clientId, ruleSplitsFile);
      if (response.data) {
        setRuleSplitsResult(response.data);
        // Reload rules after successful upload
        loadRules();
      } else {
        showError(response.message || 'Upload failed');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setRuleSplitsUploading(false);
    }
  };

  // Feedback modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // ============================================
  // Data Loading
  // ============================================

  const loadConfig = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await accountingService.getConfig(clientId);
      if (response.success && response.data) {
        setConfig(response.data);
        setNewCurrency(response.data.functionalCurrency);
      }
    } catch (err) {
      console.error('Error loading accounting config:', err);
    }
  }, [clientId]);

  const loadAccounts = useCallback(async () => {
    if (!clientId) return;
    setLoadingAccounts(true);
    try {
      const response = await accountingService.getAccounts(clientId, {
        type: typeFilter || undefined,
        search: searchFilter || undefined,
        includeInactive: showInactive,
      });
      if (response.success && response.data) {
        setAccounts(response.data);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      showError(t('accounting.errors.loadFailed'));
    } finally {
      setLoadingAccounts(false);
    }
  }, [clientId, typeFilter, searchFilter, showInactive, t]);

  // Load all active accounts (no filters) for rule form dropdowns
  const loadAllAccounts = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await accountingService.getAccounts(clientId, {
        includeInactive: false,
      });
      if (response.success && response.data) {
        setAllAccounts(response.data);
      }
    } catch (err) {
      console.error('Error loading all accounts for rules:', err);
    }
  }, [clientId]);

  // Load trading entities and groupings config for condition dropdowns
  const loadConditionData = useCallback(async () => {
    if (!clientId) return;
    try {
      const [entitiesResponse, groupings] = await Promise.all([
        clientService.getTradingEntities(clientId),
        groupingsService.getGroupings(clientId),
      ]);
      if (entitiesResponse.success && entitiesResponse.data) {
        setTradingEntities(entitiesResponse.data);
      }
      if (groupings) {
        setGroupingsConfig(groupings);
      }
    } catch (err) {
      console.error('Error loading condition data for rules:', err);
    }
  }, [clientId]);

  const loadCostCentres = useCallback(async () => {
    if (!clientId) return;
    setLoadingCostCentres(true);
    try {
      const response = await accountingService.getCostCentres(clientId, {
        search: ccSearchFilter || undefined,
        includeInactive: showInactiveCc,
      });
      if (response.success && response.data) {
        setCostCentres(response.data);
      }
    } catch (err) {
      console.error('Error loading cost centres:', err);
      showError(t('accounting.errors.loadCostCentresFailed'));
    } finally {
      setLoadingCostCentres(false);
    }
  }, [clientId, ccSearchFilter, showInactiveCc, t]);

  // Load all active cost centres (no filters) for rule form dropdowns
  const loadAllCostCentres = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await accountingService.getCostCentres(clientId, {
        includeInactive: false,
      });
      if (response.success && response.data) {
        setAllCostCentres(response.data);
      }
    } catch (err) {
      console.error('Error loading all cost centres for rules:', err);
    }
  }, [clientId]);

  const loadRules = useCallback(async () => {
    if (!clientId) return;
    setLoadingRules(true);
    try {
      const response = await accountingService.getRules(clientId, {
        concept: rulesConceptFilter || undefined,
        includeInactive: showInactiveRules,
      });
      if (response.success && response.data) {
        setRules(response.data);
      }
    } catch (err) {
      console.error('Error loading rules:', err);
      showError(t('accounting.errors.loadRulesFailed'));
    } finally {
      setLoadingRules(false);
    }
  }, [clientId, rulesConceptFilter, showInactiveRules, t]);

  // Load accounts data when Accounts sub-tab is active (or on first mount for config)
  useEffect(() => {
    if (activeSubTab === 'accounts') {
      loadConfig();
      loadAccounts();
      loadAllAccounts();
    }
  }, [activeSubTab, loadConfig, loadAccounts, loadAllAccounts]);

  // Load cost centres data when Cost Centres sub-tab is active
  useEffect(() => {
    if (activeSubTab === 'costCentres') {
      loadCostCentres();
    }
  }, [activeSubTab, loadCostCentres]);

  // Load rules data only when Rules sub-tab is active
  useEffect(() => {
    if (activeSubTab === 'rules') {
      loadConfig();
      loadAllAccounts();
      loadAllCostCentres();
      loadRules();
      loadConditionData();
    }
  }, [activeSubTab, loadConfig, loadAllAccounts, loadAllCostCentres, loadRules, loadConditionData]);

  // Filter accounts by currency (client-side)
  const filteredAccounts = useMemo(() => {
    if (showOtherCurrencies) {
      return accounts;
    }
    const functionalCurrency = config?.functionalCurrency;
    return accounts.filter((account) => {
      // Show accounts that have no currency set (inherit functional) or match functional currency
      return !account.currency || account.currency === functionalCurrency;
    });
  }, [accounts, showOtherCurrencies, config?.functionalCurrency]);

  // Group accounts by type for better readability
  const groupedAccounts = useMemo(() => {
    const typeOrder: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE', 'OTHER'];
    const groups: { type: AccountType; accounts: Account[] }[] = [];

    for (const type of typeOrder) {
      const typeAccounts = filteredAccounts
        .filter((a) => a.type === type)
        .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
      if (typeAccounts.length > 0) {
        groups.push({ type, accounts: typeAccounts });
      }
    }

    return groups;
  }, [filteredAccounts]);

  const toggleGroup = (type: AccountType) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const expandAllGroups = () => {
    const allTypes: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];
    setExpandedGroups(new Set(allTypes));
  };

  const collapseAllGroups = () => {
    setExpandedGroups(new Set());
  };

  // ============================================
  // Feedback Helpers
  // ============================================

  const showSuccess = (message: string) => {
    setAlertModal({
      isOpen: true,
      type: 'success',
      title: t('common.success'),
      message,
    });
  };

  const showError = (message: string) => {
    setAlertModal({
      isOpen: true,
      type: 'error',
      title: t('common.error'),
      message,
    });
  };

  const showWarning = (message: string) => {
    setAlertModal({
      isOpen: true,
      type: 'warning',
      title: t('common.warning'),
      message,
    });
  };

  const closeAlertModal = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  // ============================================
  // Config Handlers
  // ============================================

  const handleSaveCurrency = async () => {
    if (!newCurrency || newCurrency.length !== 3) {
      showError(t('accounting.errors.invalidCurrency'));
      return;
    }
    try {
      const response = await accountingService.updateConfig(clientId, {
        functionalCurrency: newCurrency.toUpperCase(),
      });
      if (response.success) {
        setConfig(response.data);
        setEditingCurrency(false);
        showSuccess(t('accounting.currencyUpdated'));
      }
    } catch (err) {
      console.error('Error updating currency:', err);
      showError(t('accounting.errors.updateFailed'));
    }
  };

  // ============================================
  // Account Handlers
  // ============================================

  const handleAddAccount = () => {
    setEditingAccount(null);
    setAccountForm({
      accountNumber: '',
      name: '',
      description: '',
      type: 'ASSET',
      currency: config?.functionalCurrency || 'CLP',
    });
    setCurrencyMode('single');
    setAccountFormErrors([]);
    setShowAccountForm(true);
  };

  const handleEditAccount = (account: Account) => {
    setEditingAccount(account);
    const isMulti = account.currency === MULTI_CURRENCY;
    setAccountForm({
      accountNumber: account.accountNumber,
      name: account.name,
      description: account.description || '',
      type: account.type,
      currency: isMulti ? '' : (account.currency || ''),
    });
    setCurrencyMode(isMulti ? 'multi' : 'single');
    setAccountFormErrors([]);
    setShowAccountForm(true);
  };

  const handleToggleActive = async (account: Account) => {
    try {
      const response = await accountingService.updateAccount(clientId, account.id, {
        isActive: !account.isActive,
      });
      if (response.success) {
        showSuccess(account.isActive ? t('accounting.accountDeactivated') : t('accounting.accountActivated'));
        loadAccounts();
        loadAllAccounts();
      }
    } catch (err) {
      console.error('Error toggling account status:', err);
      showError(t('accounting.errors.updateFailed'));
    }
  };

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'account' | 'rule';
    id: string;
    label: string;
  } | null>(null);

  const handleDeleteAccount = (account: Account) => {
    setDeleteConfirm({
      type: 'account',
      id: account.id,
      label: `${account.accountNumber} (${account.name})`,
    });
  };

  const confirmDeleteAccount = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'account') return;
    try {
      const response = await accountingService.deleteAccount(clientId, deleteConfirm.id);
      if (response.success) {
        showSuccess(t('accounting.accountDeleted'));
        loadAccounts();
        loadAllAccounts();
      }
    } catch (err: any) {
      console.error('Error deleting account:', err);
      if (err?.detail?.code === 'ACCOUNT_IN_USE') {
        const ruleList = (err.detail.rules as string[]).join(', ');
        showError(t('accounting.accountInUseByRules', { rules: ruleList }));
      } else {
        showError(err?.message || t('accounting.errors.deleteFailed', 'Failed to delete account'));
      }
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleAccountNumberChange = (value: string) => {
    setAccountForm(prev => {
      const newForm = { ...prev, accountNumber: value };
      // Auto-set type based on first digit
      if (value.length > 0 && value[0] in ACCOUNT_TYPE_PREFIXES) {
        newForm.type = ACCOUNT_TYPE_PREFIXES[value[0]];
      }
      return newForm;
    });
  };

  const validateAccountForm = (): boolean => {
    const errors: string[] = [];

    if (!accountForm.accountNumber || accountForm.accountNumber.length < 4) {
      errors.push(t('accounting.errors.accountNumberRequired'));
    }

    if (!accountForm.name) {
      errors.push(t('accounting.errors.accountNameRequired'));
    }

    const prefix = accountForm.accountNumber[0];
    if (prefix && prefix in ACCOUNT_TYPE_PREFIXES) {
      const expectedType = ACCOUNT_TYPE_PREFIXES[prefix];
      if (accountForm.type !== expectedType) {
        errors.push(t('accounting.errors.typeMismatch', {
          expected: t(`accounting.accountTypes.${expectedType.toLowerCase()}`),
          prefix
        }));
      }
    } else if (accountForm.accountNumber.length > 0) {
      errors.push(t('accounting.errors.invalidPrefix'));
    }

    // KAN-167: Validate currency for single-currency mode
    if (currencyMode === 'single') {
      const ccy = accountForm.currency?.trim();
      if (!ccy || ccy.length !== 3) {
        errors.push(t('accounting.errors.currencyRequired'));
      }
    }

    setAccountFormErrors(errors);
    return errors.length === 0;
  };

  const handleSaveAccount = async () => {
    if (!validateAccountForm()) return;

    // KAN-167: Resolve final currency value based on mode
    const resolvedCurrency = currencyMode === 'multi'
      ? MULTI_CURRENCY
      : accountForm.currency || config?.functionalCurrency || 'CLP';

    setSavingAccount(true);
    try {
      if (editingAccount) {
        // Update existing account
        const response = await accountingService.updateAccount(clientId, editingAccount.id, {
          accountNumber: accountForm.accountNumber,
          name: accountForm.name,
          description: accountForm.description || undefined,
          currency: resolvedCurrency,
        });
        if (response.success) {
          showSuccess(t('accounting.accountUpdated'));
          setShowAccountForm(false);
          loadAccounts();
          loadAllAccounts();
        }
      } else {
        // Create new account
        const payload = {
          ...accountForm,
          description: accountForm.description || undefined,
          currency: resolvedCurrency,
        };
        const response = await accountingService.createAccount(clientId, payload);
        if (response.success) {
          showSuccess(t('accounting.accountCreated'));
          setShowAccountForm(false);
          loadAccounts();
          loadAllAccounts();
        }
      }
    } catch (err: any) {
      console.error('Error saving account:', err);
      const errorMessage = err?.response?.data?.detail || t('accounting.errors.saveFailed');
      setAccountFormErrors([errorMessage]);
    } finally {
      setSavingAccount(false);
    }
  };

  // ============================================
  // Cost Centre Handlers
  // ============================================

  const handleAddCc = () => {
    setEditingCc(null);
    setCcForm({ code: '', description: '' });
    setCcFormErrors([]);
    setShowCcForm(true);
  };

  const handleEditCc = (cc: CostCentre) => {
    setEditingCc(cc);
    setCcForm({ code: cc.code, description: cc.description });
    setCcFormErrors([]);
    setShowCcForm(true);
  };

  const handleToggleCcActive = async (cc: CostCentre) => {
    try {
      const response = await accountingService.updateCostCentre(clientId, cc.id, {
        isActive: !cc.isActive,
      });
      if (response.success) {
        showSuccess(cc.isActive ? t('accounting.costCentreDeactivated') : t('accounting.costCentreActivated'));
        loadCostCentres();
        loadAllCostCentres();
      }
    } catch (err) {
      console.error('Error toggling cost centre status:', err);
      showError(t('accounting.errors.updateFailed'));
    }
  };

  const validateCcForm = (): boolean => {
    const errors: string[] = [];
    if (!ccForm.code || ccForm.code.trim().length === 0) {
      errors.push(t('accounting.errors.costCentreCodeRequired'));
    }
    if (!ccForm.description || ccForm.description.trim().length === 0) {
      errors.push(t('accounting.errors.costCentreDescRequired'));
    }
    setCcFormErrors(errors);
    return errors.length === 0;
  };

  const handleSaveCc = async () => {
    if (!validateCcForm()) return;

    setSavingCc(true);
    try {
      if (editingCc) {
        const response = await accountingService.updateCostCentre(clientId, editingCc.id, {
          code: ccForm.code,
          description: ccForm.description,
        });
        if (response.success) {
          showSuccess(t('accounting.costCentreUpdated'));
          setShowCcForm(false);
          loadCostCentres();
          loadAllCostCentres();
        }
      } else {
        const response = await accountingService.createCostCentre(clientId, ccForm);
        if (response.success) {
          showSuccess(t('accounting.costCentreCreated'));
          setShowCcForm(false);
          loadCostCentres();
          loadAllCostCentres();
        }
      }
    } catch (err: any) {
      console.error('Error saving cost centre:', err);
      const errorMessage = err?.response?.data?.detail || t('accounting.errors.saveCostCentreFailed');
      setCcFormErrors([errorMessage]);
    } finally {
      setSavingCc(false);
    }
  };

  // ============================================
  // Rules Handlers
  // ============================================

  const CONCEPTS: AccountingConcept[] = ['NEW_TRADE', 'VALUATION', 'VALUATION_REVERSAL', 'EXPIRY', 'MODIFICATION_CANCELLED', 'UNWIND'];

  // All active accounts for debit/credit dropdowns (unfiltered by accounts tab filters)
  const allActiveAccounts = useMemo(() => {
    return allAccounts.filter((a) => a.isActive).sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
  }, [allAccounts]);

  // Filtered rules (client-side search)
  const filteredRules = useMemo(() => {
    if (!rulesSearchFilter) return rules;
    const search = rulesSearchFilter.toLowerCase();
    return rules.filter((r) =>
      r.name.toLowerCase().includes(search) ||
      (r.description || '').toLowerCase().includes(search) ||
      r.debitAccount.includes(search) ||
      r.creditAccount.includes(search)
    );
  }, [rules, rulesSearchFilter]);

  // Group rules by concept for collapsible sections
  const groupedRules = useMemo(() => {
    const groups: { concept: AccountingConcept; rules: AccountingRule[] }[] = [];

    for (const concept of CONCEPTS) {
      const conceptRules = filteredRules.filter((r) => r.concept === concept);
      if (conceptRules.length > 0) {
        groups.push({ concept, rules: conceptRules });
      }
    }

    return groups;
  }, [filteredRules]);

  const toggleRuleGroup = (concept: AccountingConcept) => {
    setExpandedRuleGroups((prev) => {
      const next = new Set(prev);
      if (next.has(concept)) {
        next.delete(concept);
      } else {
        next.add(concept);
      }
      return next;
    });
  };

  const expandAllRuleGroups = () => {
    setExpandedRuleGroups(new Set(CONCEPTS));
  };

  const collapseAllRuleGroups = () => {
    setExpandedRuleGroups(new Set());
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setRuleForm({
      name: '',
      description: '',
      concept: 'NEW_TRADE',
      conditions: undefined,
      debitAccount: '',
      creditAccount: '',
      debitCostCentre: '',
      creditCostCentre: '',
      debitSplits: undefined,
      creditSplits: undefined,
      amountSource: 'TRADE_AMOUNT_CCY1',
      currencyHandling: 'EVENT_CURRENCY',
    });
    setRuleFormErrors([]);
    setShowRuleForm(true);
  };

  const handleEditRule = (rule: AccountingRule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      concept: rule.concept,
      conditions: rule.conditions || undefined,
      debitAccount: rule.debitSplits?.length ? '' : (rule.debitAccount || ''),
      creditAccount: rule.creditSplits?.length ? '' : (rule.creditAccount || ''),
      debitCostCentre: rule.debitSplits?.length ? '' : (rule.debitCostCentre || ''),
      creditCostCentre: rule.creditSplits?.length ? '' : (rule.creditCostCentre || ''),
      debitSplits: rule.debitSplits?.length ? rule.debitSplits.map(s => ({ ...s })) : undefined,
      creditSplits: rule.creditSplits?.length ? rule.creditSplits.map(s => ({ ...s })) : undefined,
      amountSource: rule.amountSource,
      currencyHandling: rule.currencyHandling,
      aggregated: rule.aggregated || false,
      entryMode: rule.entryMode || 'both',
      netSign: rule.netSign || undefined,
      triggerState: rule.triggerState || undefined,
      accountingDate: rule.accountingDate || undefined,
    });
    setRuleFormErrors([]);
    setShowRuleForm(true);
  };

  const handleDuplicateRule = (rule: AccountingRule) => {
    setEditingRule(null);
    setRuleForm({
      name: `${rule.name} - Copia`,
      description: rule.description || '',
      concept: rule.concept,
      conditions: rule.conditions ? { ...rule.conditions } : undefined,
      debitAccount: rule.debitSplits?.length ? '' : (rule.debitAccount || ''),
      creditAccount: rule.creditSplits?.length ? '' : (rule.creditAccount || ''),
      debitCostCentre: rule.debitSplits?.length ? '' : (rule.debitCostCentre || ''),
      creditCostCentre: rule.creditSplits?.length ? '' : (rule.creditCostCentre || ''),
      debitSplits: rule.debitSplits?.length ? rule.debitSplits.map(s => ({ ...s })) : undefined,
      creditSplits: rule.creditSplits?.length ? rule.creditSplits.map(s => ({ ...s })) : undefined,
      amountSource: rule.amountSource,
      currencyHandling: rule.currencyHandling,
      aggregated: rule.aggregated || false,
      entryMode: rule.entryMode || 'both',
      netSign: rule.netSign || undefined,
      triggerState: rule.triggerState || undefined,
      accountingDate: rule.accountingDate || undefined,
    });
    setRuleFormErrors([]);
    setShowRuleForm(true);
  };

  const handleToggleRuleActive = async (rule: AccountingRule) => {
    try {
      const response = await accountingService.updateRule(clientId, rule.id, {
        isActive: !rule.isActive,
      });
      if (response.success) {
        showSuccess(rule.isActive ? t('accounting.ruleDeactivated') : t('accounting.ruleActivated'));
        loadRules();
      }
    } catch (err) {
      console.error('Error toggling rule status:', err);
      showError(t('accounting.errors.updateFailed'));
    }
  };

  const handleDeleteRule = (rule: AccountingRule) => {
    setDeleteConfirm({
      type: 'rule',
      id: rule.id,
      label: rule.name,
    });
  };

  const confirmDeleteRule = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'rule') return;
    try {
      const response = await accountingService.deleteRule(clientId, deleteConfirm.id);
      if (response.success) {
        showSuccess(t('accounting.ruleDeleted'));
        loadRules();
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
      showError(t('accounting.errors.deleteRuleFailed', 'Failed to delete rule'));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const validateRuleForm = (): boolean => {
    const errors: string[] = [];
    const mode = ruleForm.entryMode || 'both';
    if (!ruleForm.name) errors.push(t('accounting.errors.ruleNameRequired'));

    // Debit side
    if (mode !== 'credit_only') {
      if (ruleForm.debitSplits) {
        const splitErrors = validateSplits(ruleForm.debitSplits, t('accounting.debitAccount'));
        errors.push(...splitErrors);
      } else if (!ruleForm.debitAccount) {
        errors.push(t('accounting.errors.debitAccountRequired'));
      }
    }

    // Credit side
    if (mode !== 'debit_only') {
      if (ruleForm.creditSplits) {
        const splitErrors = validateSplits(ruleForm.creditSplits, t('accounting.creditAccount'));
        errors.push(...splitErrors);
      } else if (!ruleForm.creditAccount) {
        errors.push(t('accounting.errors.creditAccountRequired'));
      }
    }

    setRuleFormErrors(errors);
    return errors.length === 0;
  };

  const validateSplits = (splits: AccountingSplit[], sideLabel: string): string[] => {
    const errors: string[] = [];
    splits.forEach((s, i) => {
      if (!s.account) errors.push(t('accounting.errors.splitAccountRequired', { side: sideLabel, n: i + 1 }));
    });
    const total = splits.reduce((sum, s) => sum + (s.percentage || 0), 0);
    if (Math.abs(total - 100) > 0.01) {
      errors.push(t('accounting.errors.splitSumInvalid', { side: sideLabel, total: total.toFixed(2) }));
    }
    return errors;
  };

  const handleSaveRule = async () => {
    if (!validateRuleForm()) return;

    setSavingRule(true);
    try {
      // Inject forced settlement type from concept constraints
      const forced = CONCEPT_FORCED_SETTLEMENT[ruleForm.concept];
      const conditionsWithForced = {
        ...ruleForm.conditions,
        ...(forced ? { settlementType: forced } : {}),
      };
      // Clean conditions: remove empty string values
      const cleanConditions = Object.fromEntries(
        Object.entries(conditionsWithForced).filter(([, v]) => v !== '' && v !== undefined && v !== null)
      ) as RuleConditions;
      const hasConditions = Object.keys(cleanConditions).length > 0;

      if (editingRule) {
        const entryMode = ruleForm.entryMode || 'both';
        const response = await accountingService.updateRule(clientId, editingRule.id, {
          name: ruleForm.name,
          description: ruleForm.description || undefined,
          conditions: hasConditions ? cleanConditions : undefined,
          debitAccount: (entryMode !== 'credit_only' && !ruleForm.debitSplits) ? ruleForm.debitAccount : '',
          creditAccount: (entryMode !== 'debit_only' && !ruleForm.creditSplits) ? ruleForm.creditAccount : '',
          debitCostCentre: (entryMode !== 'credit_only' && !ruleForm.debitSplits) ? ruleForm.debitCostCentre || '' : '',
          creditCostCentre: (entryMode !== 'debit_only' && !ruleForm.creditSplits) ? ruleForm.creditCostCentre || '' : '',
          debitSplits: entryMode !== 'credit_only' ? ruleForm.debitSplits : undefined,
          creditSplits: entryMode !== 'debit_only' ? ruleForm.creditSplits : undefined,
          amountSource: ruleForm.amountSource,
          currencyHandling: ruleForm.currencyHandling,
          aggregated: ruleForm.aggregated,
          entryMode,
          netSign: ruleForm.aggregated ? ruleForm.netSign || undefined : undefined,
          triggerState: ruleForm.triggerState || undefined,
          accountingDate: ruleForm.accountingDate || undefined,
        });
        if (response.success) {
          // KAN-167: Check for currency warnings
          const warnings = response.data?.warnings;
          if (warnings && warnings.length > 0) {
            showWarning(warnings.join('\n'));
          } else {
            showSuccess(t('accounting.ruleUpdated'));
          }
          setShowRuleForm(false);
          loadRules();
        }
      } else {
        const payload = {
          ...ruleForm,
          description: ruleForm.description || undefined,
          conditions: hasConditions ? cleanConditions : undefined,
          netSign: ruleForm.aggregated ? ruleForm.netSign || undefined : undefined,
        };
        const response = await accountingService.createRule(clientId, payload);
        if (response.success) {
          // KAN-167: Check for currency warnings
          const warnings = response.data?.warnings;
          if (warnings && warnings.length > 0) {
            showWarning(warnings.join('\n'));
          } else {
            showSuccess(t('accounting.ruleCreated'));
          }
          setShowRuleForm(false);
          loadRules();
        }
      }
    } catch (err: any) {
      console.error('Error saving rule:', err);
      const errorMessage = err?.message || t('accounting.errors.saveRuleFailed');
      setRuleFormErrors([errorMessage]);
    } finally {
      setSavingRule(false);
    }
  };

  const updateCondition = (field: keyof RuleConditions, value: string) => {
    setRuleForm((prev) => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: value || undefined,
      },
    }));
  };

  const getConditionsSummary = (conditions?: RuleConditions): string => {
    if (!conditions) return t('accounting.noConditions');
    const parts: string[] = [];
    if (conditions.productType) parts.push(t(`accounting.conditionValues.${conditions.productType}`));
    if (conditions.productSubType) {
      if (conditions.productSubType === 'AVERAGE_RATE_FORWARD') {
        parts.push(t('accounting.conditionValues.averageRateForward'));
      } else if (conditions.productSubType === 'FORWARD_STARTING') {
        parts.push(t('accounting.conditionValues.forwardStarting'));
      } else {
        parts.push(t('accounting.conditionValues.standardForward'));
      }
    }
    if (conditions.settlementType) parts.push(t(`accounting.conditionValues.${conditions.settlementType}`));
    if (conditions.direction) parts.push(t(`accounting.conditionValues.${conditions.direction}`));
    if (conditions.currency1) {
      const neg = conditions.currency1Operator === 'not_equals' ? '≠' : '';
      parts.push(`CCY1: ${neg}${conditions.currency1}`);
    }
    if (conditions.currency2) {
      const neg = conditions.currency2Operator === 'not_equals' ? '≠' : '';
      parts.push(`CCY2: ${neg}${conditions.currency2}`);
    }
    if (conditions.cashflowCurrency) parts.push(`CF: ${conditions.cashflowCurrency}`);
    if (conditions.amountSign) parts.push(t(`accounting.conditionValues.${conditions.amountSign}`));
    if (conditions.isInternalTrade !== undefined) {
      parts.push(conditions.isInternalTrade ? t('accounting.conditionValues.internalOnly') : t('accounting.conditionValues.externalOnly'));
    }
    if (conditions.ourPartyId) {
      const entity = tradingEntities.find((e) => e.id === conditions.ourPartyId);
      const name = entity ? entity.name : conditions.ourPartyId;
      const neg = conditions.ourPartyIdOperator === 'not_equals' ? '≠ ' : '';
      parts.push(`${neg}${name}`);
    }
    if (conditions.grouping1) {
      const neg = conditions.grouping1Operator === 'not_equals' ? '≠' : '';
      parts.push(`G1: ${neg}${conditions.grouping1}`);
    }
    if (conditions.grouping2) {
      const neg = conditions.grouping2Operator === 'not_equals' ? '≠' : '';
      parts.push(`G2: ${neg}${conditions.grouping2}`);
    }
    if (conditions.grouping3) {
      const neg = conditions.grouping3Operator === 'not_equals' ? '≠' : '';
      parts.push(`G3: ${neg}${conditions.grouping3}`);
    }
    return parts.length > 0 ? parts.join(', ') : t('accounting.noConditions');
  };

  // Helper: find account name by number (uses unfiltered accounts)
  const getAccountLabel = (accountNumber: string): string => {
    const account = allAccounts.find((a) => a.accountNumber === accountNumber);
    return account ? `${accountNumber} - ${account.name}` : accountNumber;
  };

  // ============================================
  // Render Helpers
  // ============================================

  const getAccountTypeLabel = (type: AccountType): string => {
    return t(`accounting.accountTypes.${type.toLowerCase()}`);
  };

  const renderAccountsSubTab = () => (
    <div className="accounting-config-content">
      {/* Config Section */}
      <div className="config-section">
        <div className="config-row">
          <span className="config-label">{t('accounting.functionalCurrency')}:</span>
          {editingCurrency ? (
            <div className="config-edit">
              <input
                type="text"
                value={newCurrency}
                onChange={(e) => setNewCurrency(e.target.value.toUpperCase())}
                maxLength={3}
                placeholder="CLP"
                className="currency-input"
              />
              <button onClick={handleSaveCurrency} className="btn-small btn-primary">
                {t('common.save')}
              </button>
              <button onClick={() => { setEditingCurrency(false); setNewCurrency(config?.functionalCurrency || 'CLP'); }} className="btn-small btn-secondary">
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <div className="config-value">
              <span className="currency-badge">{config?.functionalCurrency || 'CLP'}</span>
              <button onClick={() => setEditingCurrency(true)} className="btn-link">
                {t('common.edit')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="filters">
          <input
            type="text"
            placeholder={t('accounting.searchAccounts')}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="search-input"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="type-filter"
          >
            <option value="">{t('accounting.allTypes')}</option>
            <option value="ASSET">{t('accounting.accountTypes.asset')}</option>
            <option value="LIABILITY">{t('accounting.accountTypes.liability')}</option>
            <option value="EQUITY">{t('accounting.accountTypes.equity')}</option>
            <option value="REVENUE">{t('accounting.accountTypes.revenue')}</option>
            <option value="EXPENSE">{t('accounting.accountTypes.expense')}</option>
            <option value="OTHER">{t('accounting.accountTypes.other')}</option>
          </select>
          <button onClick={expandAllGroups} className="btn-secondary btn-small">
            {t('accounting.expandAll')}
          </button>
          <button onClick={collapseAllGroups} className="btn-secondary btn-small">
            {t('accounting.collapseAll')}
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            {t('accounting.showInactive')}
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showOtherCurrencies}
              onChange={(e) => setShowOtherCurrencies(e.target.checked)}
            />
            {t('accounting.showOtherCurrencies')}
          </label>
        </div>
        <button onClick={handleAddAccount} className="btn-primary">
          + {t('accounting.addAccount')}
        </button>
      </div>

      {/* Accounts Table */}
      <div className="accounts-table-container">
        {loadingAccounts ? (
          <div className="tab-loading">
            <div className="spinner" />
            <p>{t('common.loading')}</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="empty-message">
            <p>{t('accounting.noAccounts')}</p>
          </div>
        ) : (
          <table className="accounts-table">
            <thead>
              <tr>
                <th>{t('accounting.accountNumber')}</th>
                <th>{t('accounting.accountName')}</th>
                <th>{t('accounting.currency')}</th>
                <th>{t('accounting.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {groupedAccounts.map((group) => {
                const isExpanded = expandedGroups.has(group.type);
                return (
                  <React.Fragment key={group.type}>
                    <tr className="group-header-row" onClick={() => toggleGroup(group.type)}>
                      <td colSpan={5} className="group-header">
                        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                        {getAccountTypeLabel(group.type)}
                        <span className="group-count">({group.accounts.length})</span>
                      </td>
                    </tr>
                    {isExpanded && group.accounts.map((account) => (
                      <tr key={account.id} className={!account.isActive ? 'inactive-row' : ''}>
                        <td className="account-number">{account.accountNumber}</td>
                        <td>{account.name}</td>
                        <td>
                          {account.currency === MULTI_CURRENCY
                            ? <span className="badge badge-multi">{t('accounting.multiCurrency')}</span>
                            : (account.currency || config?.functionalCurrency || '-')}
                        </td>
                        <td>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={account.isActive}
                              onChange={() => handleToggleActive(account)}
                            />
                            <span className="slider"></span>
                          </label>
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleEditAccount(account)}
                            className="edit-button"
                            title={t('common.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(account)}
                            className="edit-button delete-button"
                            title={t('common.delete')}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-footer">
        {t('accounting.showingAccounts', { count: filteredAccounts.length })}
      </div>
    </div>
  );

  // Concept-driven constraints
  const CONCEPT_AMOUNT_SOURCES: Record<AccountingConcept, AmountSource[]> = {
    NEW_TRADE: ['TRADE_AMOUNT_CCY1', 'TRADE_AMOUNT_CCY2'],
    VALUATION: ['VALUATION_AMOUNT', 'SPOT_ELEMENT', 'FORWARD_ELEMENT'],
    VALUATION_REVERSAL: ['VALUATION_AMOUNT', 'SPOT_ELEMENT', 'FORWARD_ELEMENT'],
    EXPIRY: ['TRADE_AMOUNT_CCY1', 'TRADE_AMOUNT_CCY2', 'SETTLEMENT_AMOUNT', 'CASHFLOW_AMOUNT'],
    MODIFICATION_CANCELLED: ['TRADE_AMOUNT_CCY1', 'TRADE_AMOUNT_CCY2', 'VALUATION_AMOUNT'],
    UNWIND: ['SETTLEMENT_AMOUNT', 'TRADE_AMOUNT_CCY1', 'TRADE_AMOUNT_CCY2'],
  };

  const CONCEPT_ALLOWED_PRODUCTS: Record<AccountingConcept, string[]> = {
    NEW_TRADE: ['Spot', 'Forward'],
    VALUATION: ['Forward'],
    VALUATION_REVERSAL: ['Forward'],
    EXPIRY: ['Spot', 'Forward'],
    MODIFICATION_CANCELLED: ['Spot', 'Forward'],
    UNWIND: ['Forward'],
  };

  const CONCEPT_FORCED_SETTLEMENT: Record<AccountingConcept, string | null> = {
    NEW_TRADE: null, // both allowed
    VALUATION: null,
    VALUATION_REVERSAL: null,
    EXPIRY: null, // user picks settlementType in rule conditions
    MODIFICATION_CANCELLED: null,
    UNWIND: null,
  };

  // All concepts can use amount sign to differentiate gain vs loss rules
  const CONCEPT_SHOWS_SIGN: Set<AccountingConcept> = new Set([
    'NEW_TRADE', 'VALUATION', 'VALUATION_REVERSAL', 'EXPIRY', 'MODIFICATION_CANCELLED', 'UNWIND',
  ]);

  const renderCostCentresSubTab = () => (
    <div className="accounting-config-content">
      {/* Toolbar */}
      <div className="toolbar">
        <div className="filters">
          <input
            type="text"
            placeholder={t('accounting.searchCostCentres')}
            value={ccSearchFilter}
            onChange={(e) => setCcSearchFilter(e.target.value)}
            className="search-input"
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactiveCc}
              onChange={(e) => setShowInactiveCc(e.target.checked)}
            />
            {t('accounting.showInactive')}
          </label>
        </div>
        <button className="btn-primary" onClick={handleAddCc}>
          + {t('accounting.addCostCentre')}
        </button>
      </div>

      {/* Cost Centres Table */}
      <div className="accounts-table-container">
        {loadingCostCentres ? (
          <div className="tab-loading">
            <div className="spinner" />
            <p>{t('common.loading')}</p>
          </div>
        ) : costCentres.length === 0 ? (
          <div className="empty-message">
            <p>{t('accounting.noCostCentres')}</p>
          </div>
        ) : (
          <table className="accounts-table">
            <thead>
              <tr>
                <th>{t('accounting.costCentreCode')}</th>
                <th>{t('accounting.costCentreDescription')}</th>
                <th>{t('accounting.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {costCentres.map((cc) => (
                <tr key={cc.id} className={!cc.isActive ? 'inactive-row' : ''}>
                  <td className="account-number">{cc.code}</td>
                  <td>{cc.description}</td>
                  <td>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={cc.isActive}
                        onChange={() => handleToggleCcActive(cc)}
                      />
                      <span className="slider"></span>
                    </label>
                  </td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleEditCc(cc)}
                      className="edit-button"
                      title={t('common.edit')}
                    >
                      ✏️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-footer">
        {t('accounting.showingCostCentres', { count: costCentres.length })}
      </div>

      {/* Add/Edit Cost Centre Modal */}
      {showCcForm && (
        <div className="modal-overlay" onClick={() => setShowCcForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>{editingCc ? t('accounting.editCostCentre') : t('accounting.addCostCentre')}</h3>
              <button className="modal-close" onClick={() => setShowCcForm(false)}>×</button>
            </div>
            <div className="modal-body">
              {ccFormErrors.length > 0 && (
                <div className="form-errors">
                  {ccFormErrors.map((err, i) => (
                    <div key={i} className="form-error">{err}</div>
                  ))}
                </div>
              )}
              <div className="form-group">
                <label>{t('accounting.costCentreCode')} *</label>
                <input
                  type="text"
                  value={ccForm.code}
                  onChange={(e) => setCcForm((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder={t('accounting.costCentreCodePlaceholder')}
                  maxLength={20}
                />
              </div>
              <div className="form-group">
                <label>{t('accounting.costCentreDescription')} *</label>
                <input
                  type="text"
                  value={ccForm.description}
                  onChange={(e) => setCcForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder={t('accounting.costCentreDescriptionPlaceholder')}
                  maxLength={200}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCcForm(false)} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button onClick={handleSaveCc} className="btn-primary" disabled={savingCc}>
                {savingCc ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderRulesSubTab = () => (
    <div className="accounting-config-content">
      {/* Toolbar */}
      <div className="toolbar">
        <div className="filters">
          <input
            type="text"
            placeholder={t('accounting.searchRules')}
            value={rulesSearchFilter}
            onChange={(e) => setRulesSearchFilter(e.target.value)}
            className="search-input"
          />
          <select
            value={rulesConceptFilter}
            onChange={(e) => setRulesConceptFilter(e.target.value)}
            className="type-filter"
          >
            <option value="">{t('accounting.allConcepts')}</option>
            {CONCEPTS.map((c) => (
              <option key={c} value={c}>{t(`accounting.concepts.${c}`)}</option>
            ))}
          </select>
          <button onClick={expandAllRuleGroups} className="btn-secondary btn-small">
            {t('accounting.expandAll')}
          </button>
          <button onClick={collapseAllRuleGroups} className="btn-secondary btn-small">
            {t('accounting.collapseAll')}
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactiveRules}
              onChange={(e) => setShowInactiveRules(e.target.checked)}
            />
            {t('accounting.showInactive')}
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {canUploadRuleSplits && (
            <button
              onClick={() => {
                setRuleSplitsFile(null);
                setRuleSplitsResult(null);
                setShowRuleSplitsModal(true);
              }}
              className="btn-secondary"
              title="Cargar splits de reglas"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              <Upload size={14} />
              Cargar Splits de Reglas
            </button>
          )}
          <button onClick={handleAddRule} className="btn-primary">
            + {t('accounting.addRule')}
          </button>
        </div>
      </div>

      {/* Rules Table */}
      <div className="accounts-table-container">
        {loadingRules ? (
          <div className="tab-loading">
            <div className="spinner" />
            <p>{t('common.loading')}</p>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="empty-message">
            <p>{t('accounting.noRules')}</p>
          </div>
        ) : (
          <table className="accounts-table">
            <thead>
              <tr>
                <th>{t('accounting.ruleName')}</th>
                <th>{t('accounting.conditions')}</th>
                <th>{t('accounting.amountSource')}</th>
                <th>{t('accounting.debitAccount')}</th>
                <th>{t('accounting.creditAccount')}</th>
                <th>{t('accounting.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {groupedRules.map((group) => {
                const isExpanded = expandedRuleGroups.has(group.concept);
                return (
                  <React.Fragment key={group.concept}>
                    <tr className="group-header-row" onClick={() => toggleRuleGroup(group.concept)}>
                      <td colSpan={7} className="group-header">
                        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                        {t(`accounting.concepts.${group.concept}`)}
                        <span className="group-count">({group.rules.length})</span>
                      </td>
                    </tr>
                    {isExpanded && group.rules.map((rule) => (
                      <tr key={rule.id} className={!rule.isActive ? 'inactive-row' : ''}>
                        <td>
                          <strong>{rule.name}</strong>
                          {rule.aggregated && (
                            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', padding: '0.1rem 0.35rem', borderRadius: '3px', background: 'var(--accent-blue)', color: '#fff', fontWeight: 600 }}>
                              {t('accounting.aggregatedBadge', 'Ag')}
                            </span>
                          )}
                          {rule.entryMode === 'debit_only' && (
                            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', padding: '0.1rem 0.35rem', borderRadius: '3px', background: '#e67e22', color: '#fff', fontWeight: 600 }}>
                              {t('accounting.debitBadge', 'Debe')}
                            </span>
                          )}
                          {rule.entryMode === 'credit_only' && (
                            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', padding: '0.1rem 0.35rem', borderRadius: '3px', background: '#27ae60', color: '#fff', fontWeight: 600 }}>
                              {t('accounting.creditBadge', 'Haber')}
                            </span>
                          )}
                          {rule.netSign && (
                            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', padding: '0.1rem 0.35rem', borderRadius: '3px', background: '#8e44ad', color: '#fff', fontWeight: 600 }}>
                              {rule.netSign === 'positive' ? 'Net +' : 'Net −'}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{getConditionsSummary(rule.conditions)}</td>
                        <td style={{ fontSize: '0.85rem' }}>{t(`accounting.amountSources.${rule.amountSource}`)}</td>
                        <td className="account-number">{rule.entryMode === 'credit_only' ? '—' : rule.debitSplits?.length ? rule.debitSplits.map(s => s.account).join(', ') : getAccountLabel(rule.debitAccount)}</td>
                        <td className="account-number">{rule.entryMode === 'debit_only' ? '—' : rule.creditSplits?.length ? rule.creditSplits.map(s => s.account).join(', ') : getAccountLabel(rule.creditAccount)}</td>
                        <td>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={rule.isActive}
                              onChange={() => handleToggleRuleActive(rule)}
                            />
                            <span className="slider"></span>
                          </label>
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleDuplicateRule(rule)}
                            className="edit-button"
                            title={t('accounting.duplicateRule', 'Duplicate')}
                          >
                            📋
                          </button>
                          <button
                            onClick={() => handleEditRule(rule)}
                            className="edit-button"
                            title={t('common.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule)}
                            className="edit-button delete-button"
                            title={t('common.delete')}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-footer">
        {t('accounting.showingRules', { count: filteredRules.length })}
      </div>
    </div>
  );

  // ============================================
  // Main Render
  // ============================================

  return (
    <div className="accounting-config-panel">
      {/* Feedback Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlertModal}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      {/* Delete Confirmation Modal */}
      <AlertModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        type="warning"
        title={deleteConfirm?.type === 'account' ? t('accounting.confirmDeleteAccountTitle') : t('accounting.confirmDeleteRuleTitle')}
        message={
          deleteConfirm?.type === 'account'
            ? t('accounting.confirmDeleteAccount', { label: deleteConfirm?.label })
            : t('accounting.confirmDeleteRule', { label: deleteConfirm?.label })
        }
        onConfirm={deleteConfirm?.type === 'account' ? confirmDeleteAccount : confirmDeleteRule}
        confirmLabel={t('common.delete')}
      />

      {/* Sub-tabs */}
      <div className="sub-tabs">
        <button
          className={`sub-tab-button ${activeSubTab === 'accounts' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('accounts')}
        >
          {t('accounting.tabs.chartOfAccounts')}
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'costCentres' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('costCentres')}
        >
          {t('accounting.tabs.costCentres')}
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('rules')}
        >
          {t('accounting.tabs.rules')}
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'outputFormats' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('outputFormats')}
        >
          {t('outputFormats.subTabLabel')}
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'generationSchedule' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('generationSchedule')}
        >
          {t('accounting.tabs.generationSchedule')}
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'modificationsettings' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('modificationsettings')}
        >
          {t('accounting.tabs.modificationSettings')}
        </button>
      </div>

      {/* Sub-tab Content */}
      {activeSubTab === 'accounts' && renderAccountsSubTab()}
      {activeSubTab === 'costCentres' && renderCostCentresSubTab()}
      {activeSubTab === 'rules' && renderRulesSubTab()}
      {activeSubTab === 'outputFormats' && (
        <OutputFormatsSubTab
          clientId={clientId}
          sourceType="ACCOUNTING_ENTRIES"
        />
      )}
      {activeSubTab === 'generationSchedule' && (
        <GenerationScheduleSubTab clientId={clientId} />
      )}
      {activeSubTab === 'modificationsettings' && (
        <ModificationSettingsSubTab clientId={clientId} />
      )}

      {/* Rule Form Modal */}
      {showRuleForm && (
        <div className="modal-overlay" onClick={() => setShowRuleForm(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRule ? t('accounting.editRule') : t('accounting.addRule')}</h2>
              <button className="close-button" onClick={() => setShowRuleForm(false)}>×</button>
            </div>

            <div className="modal-body">
              {ruleFormErrors.length > 0 && (
                <div className="form-errors">
                  {ruleFormErrors.map((error, i) => (
                    <p key={i}>{error}</p>
                  ))}
                </div>
              )}

              <div className="rule-form-grid">
                {/* Left column: Name + Concept */}
                <div className="form-group">
                  <label>{t('accounting.ruleName')} *</label>
                  <input
                    type="text"
                    value={ruleForm.name}
                    onChange={(e) => setRuleForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t('accounting.ruleNamePlaceholder')}
                    maxLength={200}
                  />
                </div>

                {/* Right column: Concept */}
                <div className="form-group">
                  <label>{t('accounting.concept')} *</label>
                  <select
                    value={ruleForm.concept}
                    onChange={(e) => {
                      const concept = e.target.value as AccountingConcept;
                      const allowedSources = CONCEPT_AMOUNT_SOURCES[concept];
                      const forced = CONCEPT_FORCED_SETTLEMENT[concept];
                      const newConditions: RuleConditions = {
                        direction: ruleForm.conditions?.direction,
                        currency1: ruleForm.conditions?.currency1,
                        currency2: ruleForm.conditions?.currency2,
                        isInternalTrade: ruleForm.conditions?.isInternalTrade,
                        ourPartyId: ruleForm.conditions?.ourPartyId,
                        grouping1: ruleForm.conditions?.grouping1,
                        grouping2: ruleForm.conditions?.grouping2,
                        grouping3: ruleForm.conditions?.grouping3,
                      };
                      if (forced) {
                        newConditions.settlementType = forced as RuleConditions['settlementType'];
                      }
                      // Preserve amountSign only for valuation concepts
                      if (CONCEPT_SHOWS_SIGN.has(concept)) {
                        newConditions.amountSign = ruleForm.conditions?.amountSign;
                      }
                      setRuleForm((prev) => ({
                        ...prev,
                        concept,
                        amountSource: allowedSources[0],
                        conditions: newConditions,
                      }));
                    }}
                    disabled={!!editingRule}
                  >
                    {CONCEPTS.map((c) => (
                      <option key={c} value={c}>{t(`accounting.concepts.${c}`)}</option>
                    ))}
                  </select>
                </div>

                {/* Description spans full width */}
                <div className="form-group form-full-width">
                  <label>{t('accounting.description')}</label>
                  <input
                    type="text"
                    value={ruleForm.description}
                    onChange={(e) => setRuleForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder={t('accounting.descriptionPlaceholder')}
                    maxLength={500}
                  />
                </div>

                {/* Conditions fieldset spans full width */}
                <fieldset className="form-full-width" style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem' }}>
                  <legend style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', padding: '0 0.5rem' }}>
                    {t('accounting.conditions')}
                  </legend>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem 0.75rem' }}>
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.productType')}</label>
                      <select
                        value={ruleForm.conditions?.productType || ''}
                        onChange={(e) => {
                          const product = e.target.value;
                          updateCondition('productType', product);
                          // Spot forces Physical Delivery
                          if (product === 'Spot') {
                            updateCondition('settlementType', 'Entrega Física');
                          }
                          // Clear productSubType when not Forward
                          if (product !== 'Forward') {
                            updateCondition('productSubType', '');
                          }
                        }}
                      >
                        <option value="">--</option>
                        {CONCEPT_ALLOWED_PRODUCTS[ruleForm.concept].map((p) => (
                          <option key={p} value={p}>{t(`accounting.conditionValues.${p}`)}</option>
                        ))}
                      </select>
                    </div>
                    {ruleForm.conditions?.productType === 'Forward' && (
                      <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                        <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.productSubType')}</label>
                        <select
                          value={ruleForm.conditions?.productSubType || ''}
                          onChange={(e) => updateCondition('productSubType', e.target.value)}
                        >
                          <option value="">--</option>
                          <option value="STANDARD">{t('accounting.conditionValues.standardForward')}</option>
                          <option value="AVERAGE_RATE_FORWARD">{t('accounting.conditionValues.averageRateForward')}</option>
                          <option value="FORWARD_STARTING">{t('accounting.conditionValues.forwardStarting')}</option>
                        </select>
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.settlementType')}</label>
                      <select
                        value={ruleForm.conditions?.settlementType || CONCEPT_FORCED_SETTLEMENT[ruleForm.concept] || ''}
                        onChange={(e) => updateCondition('settlementType', e.target.value)}
                        disabled={!!CONCEPT_FORCED_SETTLEMENT[ruleForm.concept] || ruleForm.conditions?.productType === 'Spot'}
                      >
                        <option value="">--</option>
                        <option value="Entrega Física">{t('accounting.conditionValues.Entrega Física')}</option>
                        <option value="Compensación">{t('accounting.conditionValues.Compensación')}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.direction')}</label>
                      <select
                        value={ruleForm.conditions?.direction || ''}
                        onChange={(e) => updateCondition('direction', e.target.value)}
                      >
                        <option value="">--</option>
                        <option value="Buy">{t('accounting.conditionValues.Buy')}</option>
                        <option value="Sell">{t('accounting.conditionValues.Sell')}</option>
                      </select>
                    </div>
                    {CONCEPT_SHOWS_SIGN.has(ruleForm.concept) && (
                      <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                        <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.amountSign')}</label>
                        <select
                          value={ruleForm.conditions?.amountSign || ''}
                          onChange={(e) => updateCondition('amountSign', e.target.value)}
                        >
                          <option value="">--</option>
                          <option value="positive">{t('accounting.conditionValues.positive')}</option>
                          <option value="negative">{t('accounting.conditionValues.negative')}</option>
                        </select>
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.isInternalTrade')}</label>
                      <select
                        value={ruleForm.conditions?.isInternalTrade === undefined ? '' : String(ruleForm.conditions.isInternalTrade)}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRuleForm((prev) => ({
                            ...prev,
                            conditions: {
                              ...prev.conditions,
                              isInternalTrade: val === '' ? undefined : val === 'true',
                            },
                          }));
                        }}
                      >
                        <option value="">{t('accounting.conditionValues.matchAll')}</option>
                        <option value="false">{t('accounting.conditionValues.externalOnly')}</option>
                        <option value="true">{t('accounting.conditionValues.internalOnly')}</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.hasSplits')}</label>
                      <select
                        value={ruleForm.conditions?.hasSplits === undefined ? '' : String(ruleForm.conditions.hasSplits)}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRuleForm((prev) => ({
                            ...prev,
                            conditions: {
                              ...prev.conditions,
                              hasSplits: val === '' ? undefined : val === 'true',
                              // Clear isSplitTrade when "no splits only" is selected
                              ...(val === 'false' ? { isSplitTrade: undefined } : {}),
                            },
                          }));
                        }}
                      >
                        <option value="">{t('accounting.conditionValues.matchAll')}</option>
                        <option value="false">{t('accounting.conditionValues.noSplitsOnly')}</option>
                        <option value="true">{t('accounting.conditionValues.hasSplitsOnly')}</option>
                      </select>
                    </div>
                    {ruleForm.conditions?.hasSplits !== false && (
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.isSplitTrade')}</label>
                      <select
                        value={ruleForm.conditions?.isSplitTrade === undefined ? '' : String(ruleForm.conditions.isSplitTrade)}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRuleForm((prev) => ({
                            ...prev,
                            conditions: {
                              ...prev.conditions,
                              isSplitTrade: val === '' ? undefined : val === 'true',
                            },
                          }));
                        }}
                      >
                        <option value="">{t('accounting.conditionValues.matchAll')}</option>
                        <option value="false">{t('accounting.conditionValues.nonSplitOnly')}</option>
                        <option value="true">{t('accounting.conditionValues.splitOnly')}</option>
                      </select>
                    </div>
                    )}
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.ourPartyId')}</label>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <select
                          style={{ width: '5rem', flex: '0 0 auto' }}
                          value={ruleForm.conditions?.ourPartyIdOperator || 'equals'}
                          onChange={(e) => updateCondition('ourPartyIdOperator', e.target.value)}
                          disabled={!ruleForm.conditions?.ourPartyId}
                        >
                          <option value="equals">{t('accounting.conditionOperator.equals')}</option>
                          <option value="not_equals">{t('accounting.conditionOperator.not_equals')}</option>
                        </select>
                        <select
                          style={{ flex: 1 }}
                          value={ruleForm.conditions?.ourPartyId || ''}
                          onChange={(e) => {
                            updateCondition('ourPartyId', e.target.value);
                            if (!e.target.value) updateCondition('ourPartyIdOperator', '');
                          }}
                        >
                          <option value="">--</option>
                          {tradingEntities.map((entity) => (
                            <option key={entity.id} value={entity.id}>{entity.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{groupingsConfig?.grouping1Label || t('accounting.conditionFields.grouping1')}</label>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <select
                          style={{ width: '5rem', flex: '0 0 auto' }}
                          value={ruleForm.conditions?.grouping1Operator || 'equals'}
                          onChange={(e) => updateCondition('grouping1Operator', e.target.value)}
                          disabled={!ruleForm.conditions?.grouping1}
                        >
                          <option value="equals">{t('accounting.conditionOperator.equals')}</option>
                          <option value="not_equals">{t('accounting.conditionOperator.not_equals')}</option>
                        </select>
                        <select
                          style={{ flex: 1 }}
                          value={ruleForm.conditions?.grouping1 || ''}
                          onChange={(e) => {
                            updateCondition('grouping1', e.target.value);
                            if (!e.target.value) updateCondition('grouping1Operator', '');
                          }}
                        >
                          <option value="">--</option>
                          {(groupingsConfig?.grouping1Values || []).filter((v) => v.isActive).map((v) => (
                            <option key={v.id} value={v.name}>{v.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {groupingsConfig?.grouping2Label && (
                      <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                        <label style={{ fontSize: '0.8rem' }}>{groupingsConfig.grouping2Label}</label>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <select
                            style={{ width: '5rem', flex: '0 0 auto' }}
                            value={ruleForm.conditions?.grouping2Operator || 'equals'}
                            onChange={(e) => updateCondition('grouping2Operator', e.target.value)}
                            disabled={!ruleForm.conditions?.grouping2}
                          >
                            <option value="equals">{t('accounting.conditionOperator.equals')}</option>
                            <option value="not_equals">{t('accounting.conditionOperator.not_equals')}</option>
                          </select>
                          <select
                            style={{ flex: 1 }}
                            value={ruleForm.conditions?.grouping2 || ''}
                            onChange={(e) => {
                              updateCondition('grouping2', e.target.value);
                              if (!e.target.value) updateCondition('grouping2Operator', '');
                            }}
                          >
                            <option value="">--</option>
                            {(groupingsConfig.grouping2Values || []).filter((v) => v.isActive).map((v) => (
                              <option key={v.id} value={v.name}>{v.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    {groupingsConfig?.grouping3Label && (
                      <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                        <label style={{ fontSize: '0.8rem' }}>{groupingsConfig.grouping3Label}</label>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <select
                            style={{ width: '5rem', flex: '0 0 auto' }}
                            value={ruleForm.conditions?.grouping3Operator || 'equals'}
                            onChange={(e) => updateCondition('grouping3Operator', e.target.value)}
                            disabled={!ruleForm.conditions?.grouping3}
                          >
                            <option value="equals">{t('accounting.conditionOperator.equals')}</option>
                            <option value="not_equals">{t('accounting.conditionOperator.not_equals')}</option>
                          </select>
                          <select
                            style={{ flex: 1 }}
                            value={ruleForm.conditions?.grouping3 || ''}
                            onChange={(e) => {
                              updateCondition('grouping3', e.target.value);
                              if (!e.target.value) updateCondition('grouping3Operator', '');
                            }}
                          >
                            <option value="">--</option>
                            {(groupingsConfig.grouping3Values || []).filter((v) => v.isActive).map((v) => (
                              <option key={v.id} value={v.name}>{v.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.currency1')}</label>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <select
                          style={{ width: '5rem', flex: '0 0 auto' }}
                          value={ruleForm.conditions?.currency1Operator || 'equals'}
                          onChange={(e) => updateCondition('currency1Operator', e.target.value)}
                          disabled={!ruleForm.conditions?.currency1}
                        >
                          <option value="equals">{t('accounting.conditionOperator.equals')}</option>
                          <option value="not_equals">{t('accounting.conditionOperator.not_equals')}</option>
                        </select>
                        <input
                          type="text"
                          style={{ flex: 1 }}
                          value={ruleForm.conditions?.currency1 || ''}
                          onChange={(e) => {
                            updateCondition('currency1', e.target.value.toUpperCase());
                            if (!e.target.value) updateCondition('currency1Operator', '');
                          }}
                          placeholder="USD"
                          maxLength={3}
                        />
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                      <label style={{ fontSize: '0.8rem' }}>{t('accounting.conditionFields.currency2')}</label>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <select
                          style={{ width: '5rem', flex: '0 0 auto' }}
                          value={ruleForm.conditions?.currency2Operator || 'equals'}
                          onChange={(e) => updateCondition('currency2Operator', e.target.value)}
                          disabled={!ruleForm.conditions?.currency2}
                        >
                          <option value="equals">{t('accounting.conditionOperator.equals')}</option>
                          <option value="not_equals">{t('accounting.conditionOperator.not_equals')}</option>
                        </select>
                        <input
                          type="text"
                          style={{ flex: 1 }}
                          value={ruleForm.conditions?.currency2 || ''}
                          onChange={(e) => {
                            updateCondition('currency2', e.target.value.toUpperCase());
                            if (!e.target.value) updateCondition('currency2Operator', '');
                          }}
                          placeholder="CLP"
                          maxLength={3}
                        />
                      </div>
                    </div>
                  </div>
                </fieldset>

                {/* Forward Starting Timing — shown only for NEW_TRADE rules that could match forward starting trades */}
                {ruleForm.concept === 'NEW_TRADE' && (() => {
                  const pt = ruleForm.conditions?.productType;
                  const pst = ruleForm.conditions?.productSubType;
                  // Show when rule could match forward starting trades
                  const couldMatchFwdStarting =
                    pst === 'FORWARD_STARTING' ||
                    (!pst && (pt === 'Forward' || !pt));
                  // Hide when explicitly targeting non-forward-starting
                  const explicitlyExcluded =
                    pst === 'STANDARD' || pst === 'AVERAGE_RATE_FORWARD' || pt === 'Spot';
                  return couldMatchFwdStarting && !explicitlyExcluded;
                })() && (
                  <fieldset className="form-full-width" style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem' }}>
                    <legend style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', padding: '0 0.5rem' }}>
                      {t('accounting.forwardStartingTimingSection')}
                    </legend>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem 0', lineHeight: 1.5 }}>
                      {t('accounting.forwardStartingTimingHelp')}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 0.75rem' }}>
                      <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                        <label style={{ fontSize: '0.8rem' }}>{t('accounting.triggerStateLabel')}</label>
                        <select
                          value={ruleForm.triggerState || 'confirmed'}
                          onChange={(e) => setRuleForm((prev) => ({ ...prev, triggerState: e.target.value as any || undefined }))}
                        >
                          <option value="confirmed">{t('accounting.triggerStateConfirmed')}</option>
                          <option value="booked_provisional">{t('accounting.triggerStateBookedProvisional')}</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: '0.25rem' }}>
                        <label style={{ fontSize: '0.8rem' }}>{t('accounting.accountingDateLabel')}</label>
                        <select
                          value={ruleForm.accountingDate || 'tradeDate'}
                          onChange={(e) => setRuleForm((prev) => ({ ...prev, accountingDate: e.target.value as any || undefined }))}
                        >
                          <option value="tradeDate">{t('accounting.accountingDateTradeDate')}</option>
                          <option value="valueDate">{t('accounting.accountingDateValueDate')}</option>
                        </select>
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* Journal Entry fieldset spans full width */}
                <fieldset className="form-full-width" style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.75rem', marginBottom: '0.5rem' }}>
                  <legend style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', padding: '0 0.5rem' }}>
                    {t('accounting.journalEntry')}
                  </legend>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {/* Row 1: Amount Source + Aggregate checkbox + Net Sign */}
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>{t('accounting.amountSource')} *</label>
                        {CONCEPT_AMOUNT_SOURCES[ruleForm.concept].length === 1 ? (
                          <input
                            type="text"
                            value={t(`accounting.amountSources.${ruleForm.amountSource}`)}
                            disabled
                          />
                        ) : (
                          <select
                            value={ruleForm.amountSource}
                            onChange={(e) => setRuleForm((prev) => ({ ...prev, amountSource: e.target.value as AmountSource }))}
                          >
                            {CONCEPT_AMOUNT_SOURCES[ruleForm.concept].map((s) => (
                              <option key={s} value={s}>{t(`accounting.amountSources.${s}`)}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.35rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={ruleForm.aggregated || false}
                            onChange={(e) => setRuleForm((prev) => ({ ...prev, aggregated: e.target.checked, ...(e.target.checked ? {} : { netSign: undefined }) }))}
                            style={{ width: '16px', height: '16px', accentColor: 'var(--accent-blue)' }}
                          />
                          {t('accounting.aggregateEntries', 'Aggregate')}
                        </label>
                      </div>
                      {ruleForm.aggregated && (
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                          <label>{t('accounting.netSign', 'Net sign filter')}</label>
                          <select
                            value={ruleForm.netSign || ''}
                            onChange={(e) => setRuleForm((prev) => ({ ...prev, netSign: (e.target.value || undefined) as 'positive' | 'negative' | undefined }))}
                          >
                            <option value="">{t('accounting.netSignOptions.none', '— No filter —')}</option>
                            <option value="positive">{t('accounting.netSignOptions.positive', 'Positive')}</option>
                            <option value="negative">{t('accounting.netSignOptions.negative', 'Negative')}</option>
                          </select>
                        </div>
                      )}
                    </div>
                    {/* Row 2: Entry Mode (full width) */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('accounting.entryMode')}</label>
                      <select
                        value={ruleForm.entryMode || 'both'}
                        onChange={(e) => {
                          const mode = e.target.value as EntryMode;
                          setRuleForm((prev) => ({
                            ...prev,
                            entryMode: mode,
                            ...(mode === 'credit_only' ? { debitAccount: '' } : {}),
                            ...(mode === 'debit_only' ? { creditAccount: '' } : {}),
                          }));
                        }}
                      >
                        <option value="both">{t('accounting.entryModes.both')}</option>
                        <option value="debit_only">{t('accounting.entryModes.debit_only')}</option>
                        <option value="credit_only">{t('accounting.entryModes.credit_only')}</option>
                      </select>
                    </div>
                    {/* Row 3: Accounts side by side */}
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      {(ruleForm.entryMode || 'both') !== 'credit_only' && (
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                            <label style={{ marginBottom: 0 }}>{t('accounting.debitAccount')} *</label>
                            <button
                              type="button"
                              className="btn-link"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => setRuleForm((prev) => {
                                if (prev.debitSplits) {
                                  // Switch back to simple: restore first split account/costCentre if available
                                  return { ...prev, debitAccount: prev.debitSplits[0]?.account || '', debitCostCentre: prev.debitSplits[0]?.costCentre || '', debitSplits: undefined };
                                } else {
                                  // Switch to split mode: seed two rows, first gets current account/costCentre
                                  return { ...prev, debitAccount: '', debitCostCentre: '', debitSplits: [{ account: prev.debitAccount || '', percentage: 50, costCentre: prev.debitCostCentre || undefined }, { account: '', percentage: 50 }] };
                                }
                              })}
                            >
                              {ruleForm.debitSplits ? t('accounting.splitModeOff') : t('accounting.splitModeOn')}
                            </button>
                          </div>
                          {!ruleForm.debitSplits ? (
                            <>
                              <select
                                value={ruleForm.debitAccount}
                                onChange={(e) => setRuleForm((prev) => ({ ...prev, debitAccount: e.target.value }))}
                              >
                                <option value="">{t('accounting.selectAccount')}</option>
                                {allActiveAccounts.map((a) => (
                                  <option key={a.id} value={a.accountNumber}>
                                    {a.accountNumber} - {a.name}
                                  </option>
                                ))}
                              </select>
                              {allCostCentres.length > 0 && (
                                <select
                                  value={ruleForm.debitCostCentre || ''}
                                  onChange={(e) => setRuleForm((prev) => ({ ...prev, debitCostCentre: e.target.value }))}
                                  style={{ marginTop: '0.35rem' }}
                                >
                                  <option value="">{t('accounting.selectCostCentre')}</option>
                                  {allCostCentres.map((cc) => (
                                    <option key={cc.id} value={cc.code}>{cc.code} - {cc.description}</option>
                                  ))}
                                </select>
                              )}
                            </>
                          ) : (
                            <div>
                              {ruleForm.debitSplits.map((split, i) => (
                                <div key={i} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                                  <select
                                    value={split.account}
                                    style={{ flex: 2, minWidth: '120px' }}
                                    onChange={(e) => setRuleForm((prev) => {
                                      const splits = [...(prev.debitSplits || [])];
                                      splits[i] = { ...splits[i], account: e.target.value };
                                      return { ...prev, debitSplits: splits };
                                    })}
                                  >
                                    <option value="">{t('accounting.selectAccount')}</option>
                                    {allActiveAccounts.map((a) => (
                                      <option key={a.id} value={a.accountNumber}>{a.accountNumber} - {a.name}</option>
                                    ))}
                                  </select>
                                  {allCostCentres.length > 0 && (
                                    <select
                                      value={split.costCentre || ''}
                                      style={{ flex: 1, minWidth: '100px' }}
                                      onChange={(e) => setRuleForm((prev) => {
                                        const splits = [...(prev.debitSplits || [])];
                                        splits[i] = { ...splits[i], costCentre: e.target.value || undefined };
                                        return { ...prev, debitSplits: splits };
                                      })}
                                    >
                                      <option value="">{t('accounting.selectCostCentre')}</option>
                                      {allCostCentres.map((cc) => (
                                        <option key={cc.id} value={cc.code}>{cc.code}</option>
                                      ))}
                                    </select>
                                  )}
                                  <input
                                    type="number"
                                    value={split.percentage}
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    style={{ width: '80px' }}
                                    onChange={(e) => setRuleForm((prev) => {
                                      const splits = [...(prev.debitSplits || [])];
                                      splits[i] = { ...splits[i], percentage: parseFloat(e.target.value) || 0 };
                                      return { ...prev, debitSplits: splits };
                                    })}
                                  />
                                  <span>%</span>
                                  <button type="button" className="btn-link" style={{ color: 'var(--danger)', fontSize: '1rem', lineHeight: 1 }}
                                    onClick={() => setRuleForm((prev) => {
                                      const splits = (prev.debitSplits || []).filter((_, idx) => idx !== i);
                                      return { ...prev, debitSplits: splits.length >= 1 ? splits : undefined, debitAccount: splits.length === 0 ? '' : prev.debitAccount };
                                    })}
                                  >×</button>
                                </div>
                              ))}
                              {ruleForm.debitSplits.length < 5 && (
                                <button type="button" className="btn-link" style={{ fontSize: '0.8rem' }}
                                  onClick={() => setRuleForm((prev) => ({ ...prev, debitSplits: [...(prev.debitSplits || []), { account: '', percentage: 0 }] }))}
                                >
                                  + {t('accounting.addSplit')}
                                </button>
                              )}
                              <div style={{ fontSize: '0.75rem', marginTop: '0.2rem', color: Math.abs((ruleForm.debitSplits || []).reduce((s, x) => s + (x.percentage || 0), 0) - 100) <= 0.01 ? 'var(--success)' : 'var(--danger)' }}>
                                {t('accounting.splitTotal')}: {(ruleForm.debitSplits || []).reduce((s, x) => s + (x.percentage || 0), 0).toFixed(2)}%
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {(ruleForm.entryMode || 'both') !== 'debit_only' && (
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                            <label style={{ marginBottom: 0 }}>{t('accounting.creditAccount')} *</label>
                            <button
                              type="button"
                              className="btn-link"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => setRuleForm((prev) => {
                                if (prev.creditSplits) {
                                  return { ...prev, creditAccount: prev.creditSplits[0]?.account || '', creditCostCentre: prev.creditSplits[0]?.costCentre || '', creditSplits: undefined };
                                } else {
                                  return { ...prev, creditAccount: '', creditCostCentre: '', creditSplits: [{ account: prev.creditAccount || '', percentage: 50, costCentre: prev.creditCostCentre || undefined }, { account: '', percentage: 50 }] };
                                }
                              })}
                            >
                              {ruleForm.creditSplits ? t('accounting.splitModeOff') : t('accounting.splitModeOn')}
                            </button>
                          </div>
                          {!ruleForm.creditSplits ? (
                            <>
                              <select
                                value={ruleForm.creditAccount}
                                onChange={(e) => setRuleForm((prev) => ({ ...prev, creditAccount: e.target.value }))}
                              >
                                <option value="">{t('accounting.selectAccount')}</option>
                                {allActiveAccounts.map((a) => (
                                  <option key={a.id} value={a.accountNumber}>
                                    {a.accountNumber} - {a.name}
                                  </option>
                                ))}
                              </select>
                              {allCostCentres.length > 0 && (
                                <select
                                  value={ruleForm.creditCostCentre || ''}
                                  onChange={(e) => setRuleForm((prev) => ({ ...prev, creditCostCentre: e.target.value }))}
                                  style={{ marginTop: '0.35rem' }}
                                >
                                  <option value="">{t('accounting.selectCostCentre')}</option>
                                  {allCostCentres.map((cc) => (
                                    <option key={cc.id} value={cc.code}>{cc.code} - {cc.description}</option>
                                  ))}
                                </select>
                              )}
                            </>
                          ) : (
                            <div>
                              {ruleForm.creditSplits.map((split, i) => (
                                <div key={i} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                                  <select
                                    value={split.account}
                                    style={{ flex: 2, minWidth: '120px' }}
                                    onChange={(e) => setRuleForm((prev) => {
                                      const splits = [...(prev.creditSplits || [])];
                                      splits[i] = { ...splits[i], account: e.target.value };
                                      return { ...prev, creditSplits: splits };
                                    })}
                                  >
                                    <option value="">{t('accounting.selectAccount')}</option>
                                    {allActiveAccounts.map((a) => (
                                      <option key={a.id} value={a.accountNumber}>{a.accountNumber} - {a.name}</option>
                                    ))}
                                  </select>
                                  {allCostCentres.length > 0 && (
                                    <select
                                      value={split.costCentre || ''}
                                      style={{ flex: 1, minWidth: '100px' }}
                                      onChange={(e) => setRuleForm((prev) => {
                                        const splits = [...(prev.creditSplits || [])];
                                        splits[i] = { ...splits[i], costCentre: e.target.value || undefined };
                                        return { ...prev, creditSplits: splits };
                                      })}
                                    >
                                      <option value="">{t('accounting.selectCostCentre')}</option>
                                      {allCostCentres.map((cc) => (
                                        <option key={cc.id} value={cc.code}>{cc.code}</option>
                                      ))}
                                    </select>
                                  )}
                                  <input
                                    type="number"
                                    value={split.percentage}
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    style={{ width: '80px' }}
                                    onChange={(e) => setRuleForm((prev) => {
                                      const splits = [...(prev.creditSplits || [])];
                                      splits[i] = { ...splits[i], percentage: parseFloat(e.target.value) || 0 };
                                      return { ...prev, creditSplits: splits };
                                    })}
                                  />
                                  <span>%</span>
                                  <button type="button" className="btn-link" style={{ color: 'var(--danger)', fontSize: '1rem', lineHeight: 1 }}
                                    onClick={() => setRuleForm((prev) => {
                                      const splits = (prev.creditSplits || []).filter((_, idx) => idx !== i);
                                      return { ...prev, creditSplits: splits.length >= 1 ? splits : undefined, creditAccount: splits.length === 0 ? '' : prev.creditAccount };
                                    })}
                                  >×</button>
                                </div>
                              ))}
                              {ruleForm.creditSplits.length < 5 && (
                                <button type="button" className="btn-link" style={{ fontSize: '0.8rem' }}
                                  onClick={() => setRuleForm((prev) => ({ ...prev, creditSplits: [...(prev.creditSplits || []), { account: '', percentage: 0 }] }))}
                                >
                                  + {t('accounting.addSplit')}
                                </button>
                              )}
                              <div style={{ fontSize: '0.75rem', marginTop: '0.2rem', color: Math.abs((ruleForm.creditSplits || []).reduce((s, x) => s + (x.percentage || 0), 0) - 100) <= 0.01 ? 'var(--success)' : 'var(--danger)' }}>
                                {t('accounting.splitTotal')}: {(ruleForm.creditSplits || []).reduce((s, x) => s + (x.percentage || 0), 0).toFixed(2)}%
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowRuleForm(false)} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button onClick={handleSaveRule} className="btn-primary" disabled={savingRule}>
                {savingRule ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Form Modal */}
      {showAccountForm && (
        <div className="modal-overlay" onClick={() => setShowAccountForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAccount ? t('accounting.editAccount') : t('accounting.addAccount')}</h2>
              <button className="close-button" onClick={() => setShowAccountForm(false)}>×</button>
            </div>

            <div className="modal-body">
              {accountFormErrors.length > 0 && (
                <div className="form-errors">
                  {accountFormErrors.map((error, i) => (
                    <p key={i}>{error}</p>
                  ))}
                </div>
              )}

              <div className="form-group">
                <label>{t('accounting.accountNumber')} *</label>
                <input
                  type="text"
                  value={accountForm.accountNumber}
                  onChange={(e) => handleAccountNumberChange(e.target.value)}
                  placeholder="1001"
                  maxLength={12}
                />
                <span className="hint">{t('accounting.accountNumberHint')}</span>
              </div>

              <div className="form-group">
                <label>{t('accounting.accountName')} *</label>
                <input
                  type="text"
                  value={accountForm.name}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('accounting.accountNamePlaceholder')}
                  maxLength={200}
                />
              </div>

              <div className="form-group">
                <label>{t('accounting.description')}</label>
                <textarea
                  value={accountForm.description}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('accounting.descriptionPlaceholder')}
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label>{t('accounting.accountType')} *</label>
                <select
                  value={accountForm.type}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, type: e.target.value as AccountType }))}
                  disabled={!editingAccount && accountForm.accountNumber.length > 0}
                >
                  <option value="ASSET">{t('accounting.accountTypes.asset')}</option>
                  <option value="LIABILITY">{t('accounting.accountTypes.liability')}</option>
                  <option value="EQUITY">{t('accounting.accountTypes.equity')}</option>
                  <option value="REVENUE">{t('accounting.accountTypes.revenue')}</option>
                  <option value="EXPENSE">{t('accounting.accountTypes.expense')}</option>
                  <option value="OTHER">{t('accounting.accountTypes.other')}</option>
                </select>
                {!editingAccount && accountForm.accountNumber.length > 0 && (
                  <span className="hint">{t('accounting.typeAutoSelected')}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('accounting.currencyMode')} *</label>
                <div className="currency-mode-row">
                  <select
                    value={currencyMode}
                    onChange={(e) => setCurrencyMode(e.target.value as 'single' | 'multi')}
                  >
                    <option value="single">{t('accounting.singleCurrency')}</option>
                    <option value="multi">{t('accounting.multiCurrency')}</option>
                  </select>
                  {currencyMode === 'single' && (
                    <input
                      type="text"
                      value={accountForm.currency}
                      onChange={(e) => setAccountForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                      placeholder={config?.functionalCurrency || 'CLP'}
                      maxLength={3}
                      className="currency-code-input"
                    />
                  )}
                </div>
                <span className="hint">
                  {currencyMode === 'single'
                    ? t('accounting.singleCurrencyHint')
                    : t('accounting.multiCurrencyHint')}
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowAccountForm(false)} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button onClick={handleSaveAccount} className="btn-primary" disabled={savingAccount}>
                {savingAccount ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Splits Upload Modal (Iansa-only) */}
      {showRuleSplitsModal && (
        <div className="modal-overlay" onClick={() => !ruleSplitsUploading && setShowRuleSplitsModal(false)}>
          <div className="modal-content" style={{ maxWidth: ruleSplitsResult ? '700px' : '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cargar Splits de Reglas</h2>
              <button className="close-button" onClick={() => !ruleSplitsUploading && setShowRuleSplitsModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {!ruleSplitsResult && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Archivo Excel (.xlsx)</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      setRuleSplitsFile(e.target.files?.[0] || null);
                      setRuleSplitsResult(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem',
                    }}
                  />
                </div>
              )}

              {/* Results table */}
              {ruleSplitsResult && (
                <div style={{ fontSize: '0.82rem' }}>
                  <table className="accounts-table" style={{ marginBottom: '0.75rem' }}>
                    <thead>
                      <tr>
                        <th>Regla</th>
                        <th>Lado</th>
                        <th>Cuenta</th>
                        <th style={{ textAlign: 'right' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleSplitsResult.updated.map((u, i) =>
                        u.splits.map((s, j) => (
                          <tr key={`ok-${i}-${j}`}>
                            {j === 0 && (
                              <td rowSpan={u.splits.length} style={{ verticalAlign: 'top', fontWeight: 500 }}>
                                {u.rule}
                              </td>
                            )}
                            <td>{s.side}</td>
                            <td>{s.account}</td>
                            <td style={{ textAlign: 'right' }}>{s.percentage}%</td>
                          </tr>
                        ))
                      )}
                      {ruleSplitsResult.notFound.map((name, i) => (
                        <tr key={`nf-${i}`}>
                          <td style={{ fontWeight: 500 }}>{name}</td>
                          <td colSpan={3} style={{ color: '#f39c12' }}>No encontrada</td>
                        </tr>
                      ))}
                      {ruleSplitsResult.errors.map((err, i) => (
                        <tr key={`err-${i}`}>
                          <td colSpan={4} style={{ color: '#e74c3c' }}>{err}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {ruleSplitsResult.summary.rulesUpdated} actualizadas
                    {ruleSplitsResult.summary.rulesNotFound > 0 && `, ${ruleSplitsResult.summary.rulesNotFound} no encontradas`}
                    {ruleSplitsResult.summary.errors > 0 && `, ${ruleSplitsResult.summary.errors} errores`}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowRuleSplitsModal(false)}
                disabled={ruleSplitsUploading}
                style={{ height: '32px', padding: '0 1rem', fontSize: '0.85rem' }}
              >
                Cerrar
              </button>
              {!ruleSplitsResult && (
                <button
                  onClick={handleRuleSplitsUpload}
                  disabled={ruleSplitsUploading || !ruleSplitsFile}
                  style={{
                    background: '#3498db',
                    color: '#fff',
                    border: 'none',
                    padding: '0 1rem',
                    height: '32px',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    cursor: (ruleSplitsUploading || !ruleSplitsFile) ? 'not-allowed' : 'pointer',
                    opacity: (ruleSplitsUploading || !ruleSplitsFile) ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                  }}
                >
                  {ruleSplitsUploading ? 'Cargando...' : 'Cargar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AccountingConfigPanel;
